import { useState, type MouseEvent } from 'react';
import { useSession } from '../lib/session';
import { toggleWatch } from '../lib/queries';
import './WatchButton.css';

interface WatchButtonProps {
  postId: string;
  initialWatching: boolean;
  /** Pass watch_count from the auction state. */
  initialCount: number;
  /** compact = icon + count; full = labeled; overlay = bare heart icon (no
   *  count, no pill) for the top-right of a card image. Defaults to 'compact'. */
  variant?: 'compact' | 'full' | 'overlay';
  className?: string;
  /** When false (e.g. an ended lot), you can switch watching OFF but not back
   *  ON - the button only renders while currently watching, then disappears. */
  canWatch?: boolean;
}

/**
 * Heart-toggle button that flips watch state on a post.
 *
 * Optimistic: local watching + count update first, then call the toggleWatch
 * query and roll back on error. Disabled (still rendered) when there is no
 * signed-in profile so the button can sit on a Sales Room card without
 * crashing for not-yet-loaded sessions.
 *
 * Visuals match the polls/like pulse - ♡ at rest, ♥ in accent when on.
 */
export function WatchButton({
  postId,
  initialWatching,
  initialCount,
  variant = 'compact',
  className,
  canWatch = true,
}: WatchButtonProps) {
  const { profile } = useSession();
  const [watching, setWatching] = useState(initialWatching);
  const [count, setCount] = useState(initialCount);
  const [busy, setBusy] = useState(false);

  const disabled = !profile || busy;

  async function onClick(e: MouseEvent<HTMLButtonElement>) {
    // The button often sits inside a <Link to=...> wrapping the whole card.
    // Stop the click bubbling so the navigation doesn't fire when the heart
    // is tapped.
    e.preventDefault();
    e.stopPropagation();
    if (!profile || busy) return;

    const prevWatching = watching;
    const prevCount = count;
    // Optimistic flip
    setWatching(!prevWatching);
    setCount(prevCount + (prevWatching ? -1 : 1));
    setBusy(true);
    try {
      await toggleWatch(postId, profile.id, prevWatching);
    } catch (err) {
      console.warn('[WatchButton] toggle failed', err);
      // Roll back
      setWatching(prevWatching);
      setCount(prevCount);
    } finally {
      setBusy(false);
    }
  }

  // Ended lot: once you've stopped watching there's no watching it again, so
  // the control simply isn't there for non-watchers.
  if (!canWatch && !watching) return null;

  const label = watching ? 'Remove from favorites' : 'Add to favorites';

  const isOverlay = variant === 'overlay';

  return (
    <button
      type="button"
      className={
        isOverlay
          ? `lot-fav ${watching ? 'lot-fav--on' : ''} ${className ?? ''}`
          : `watch-btn watch-btn--${variant} ${watching ? 'watch-btn--on' : ''} ${className ?? ''}`
      }
      onClick={onClick}
      disabled={disabled}
      aria-pressed={watching}
      aria-label={label}
      title={label}
    >
      {/* Overlay (over a photo): the heart is ALWAYS filled - white at rest,
          accent when favorited (colour comes from .lot-fav / .lot-fav--on).
          Inline (post-like): outline at rest, fills accent when favorited. */}
      <span className="watch-btn__heart" aria-hidden="true"><HeartIcon on={isOverlay ? true : watching} size={isOverlay ? 20 : 22} /></span>
      {/* Overlay is icon-only; compact/full show the count. */}
      {!isOverlay &&
        (variant === 'full' ? (
          <span className="watch-btn__label">
            {watching ? 'Favorited' : 'Favorite'}
            <span aria-hidden="true"> &middot; </span>
            <span className="watch-btn__count">{count}</span>
          </span>
        ) : (
          <span className="watch-btn__count">{count}</span>
        ))}
    </button>
  );
}

/** The one heart icon used across favorites/likes. A SINGLE path - filled when
 *  `on`, outlined when off - so the two states are the same shape (the text
 *  glyphs ♥/♡ are different characters and don't match). Filled by default for
 *  the Saleroom header's Favorites pill. */
export function HeartIcon({ on = true, size = 16 }: { on?: boolean; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={on ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M12 20.5C12 20.5 3.5 15.5 3.5 9.4C3.5 6.5 5.8 4.7 8.1 4.7C9.8 4.7 11.2 5.7 12 7C12.8 5.7 14.2 4.7 15.9 4.7C18.2 4.7 20.5 6.5 20.5 9.4C20.5 15.5 12 20.5 12 20.5Z" />
    </svg>
  );
}
